import React, { useMemo } from 'react';
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { FormattedMessage, injectIntl } from "react-intl";

import { featureInterface } from "./featureInterface";
import TextInput from "../../Generic/textInput";
import { fixFeatureNumberValueForInput, getFeatureEditPermission } from "../featuresFunctions";
import { featuresSetupUpdateFeature } from "../featuresActions";
import FeaturesSetupFeatureContentUnitSelect from "./FeaturesSetupFeatureContentUnitSelect";
import FeaturesSetupFeatureContentMarkUpButton from "./FeaturesSetupFeatureContentMarkUpButton";
import { MARK_UP_APPLY_TO_TYPES } from "../FeaturesSetupMarkPopup/markUpItemInterface";
import { AccessRights, PARTS } from '../../../schemas/accessRightsModel';
import FeaturesSetupFeatureContentSku from "./FeaturesSetupFeatureContentSku";
import FeaturesSetupFeatureContentCurrencySelect from "./FeaturesSetupFeatureContentCurrencySelect";

const FeaturesSetupFeatureContent = (props) => {

  const { feature, intl, canEdit, featuresSetupUpdateFeature, AR, useDifferentCurrencies, sharedFeatureNameToggleValue } = props;
  const costDisabled = feature.priceLocked || (!AR.v(PARTS.PROJECT_SCOPE_COST) && !AR.isSiteAdmin(true));

  const featureContentInputChange = (property, value, oldValue) => {
    if (!canEdit) {
      return false
    }
    if ((property === "cost" || property === "price") && feature.priceLocked) {
      return false
    }
    if (feature.markUpLogic && (value !== +oldValue)) { // simple blur
      const parsedFeatureLogic = JSON.parse(feature.markUpLogic);
      const newFeatureLogic = parsedFeatureLogic.filter(markItem => {
        if (feature.unlinkedData && markItem.forUnlinkedCatId && (markItem.forUnlinkedCatId !== +feature.unlinkedData.featureGroupCategory_id)) {
          return true
        }
        if (property === "cost") {
          return markItem.applyTo !== MARK_UP_APPLY_TO_TYPES.cost && markItem.applyTo !== MARK_UP_APPLY_TO_TYPES.retailPriceAndCost
        } else if (property === "price") {
          return markItem.applyTo !== MARK_UP_APPLY_TO_TYPES.retailPrice && markItem.applyTo !== MARK_UP_APPLY_TO_TYPES.retailPriceAndCost
        }
        return true
      })
      if (newFeatureLogic.length !== parsedFeatureLogic.length) {
        featuresSetupUpdateFeature(feature, "markUpLogic", newFeatureLogic);
      }
    }
    featuresSetupUpdateFeature(feature, property, value);
  }

  const nameValue = feature.unlinkedData && feature.unlinkedData.displayName ? feature.unlinkedData.displayName : feature.name;
  const sharedFeatureNameValue = feature.sharedFeatureName;
  const priceValue = useMemo(() => fixFeatureNumberValueForInput(feature, "price"),
    [feature.price, feature.unlinkedData ? feature.unlinkedData.price : null]);
  const costValue = useMemo(() => fixFeatureNumberValueForInput(feature, "cost"),
    [feature.cost, feature.unlinkedData ? feature.unlinkedData.cost : null]);
  const fabricatorCost = useMemo(() => fixFeatureNumberValueForInput(feature, "fabricatorCost"),
    [feature.fabricatorCost, feature.unlinkedData ? feature.unlinkedData.fabricatorCost : null]);

  return (
    <div className="fs-feature__content">
      {sharedFeatureNameToggleValue
        ? <div className="fs-feature__line">
          <TextInput className={"ui-input ui-input--gray4 ui-input--gray4--small text-left"}
            placeholder={"Shared Feature Name"}
            onChange={(value) => featureContentInputChange("sharedFeatureName", value)}
            value={sharedFeatureNameValue} />
        </div>
        : <div className="fs-feature__line">
          <TextInput className={"ui-input ui-input--gray4 ui-input--gray4--small text-left" + (canEdit ? "" : " ui-input--gray4--disabled")}
            placeholder={intl.formatMessage({ id: "features.setup.feature.placeholder.name" })}
            readonly={!canEdit}
            onChange={(value) => featureContentInputChange("name", value)}
            value={nameValue} />
        </div>}
      <div className="fs-feature__line">
        <div className={"fs-feature__line-item" + (useDifferentCurrencies ? " fs-feature__line-item--cost" : " fs-feature__line-item--price")}>
          {/* retailPrice is an old property, we always used that // the medium value */}
          <TextInput className={`ui-input ui-input--gray4 ${feature.priceLocked ? "ui-input--gray4--disabled" : ""} ui-input--gray4--small text-left`}
            placeholder={intl.formatMessage({ id: "features.setup.feature.placeholder.retailPrice" })}
            onChange={(value) => featureContentInputChange("cost", value, costValue)}
            maxLength={10}
            disabled={feature.priceLocked}
            numberInput={true}
            value={costValue} />
          <FeaturesSetupFeatureContentMarkUpButton feature={feature}
            applyToProps={[MARK_UP_APPLY_TO_TYPES.cost,
            MARK_UP_APPLY_TO_TYPES.retailPriceAndCost]} />
        </div>
        {useDifferentCurrencies ? (
          <div className="fs-feature__line-item">
            <FeaturesSetupFeatureContentCurrencySelect feature={feature} />
          </div>
        ) : null}
        <div className="fs-feature__line-item">
          <FeaturesSetupFeatureContentUnitSelect feature={feature} />
        </div>
      </div>
      <div className="fs-feature__line">
        <div className="fs-feature__line-item fs-feature__line-item--cost">
          {/* fabricatorCost is a new property that we added, it is a Discount placeholder (strikedthrough value on the frontend) // the highest value */}
          <TextInput className={`ui-input ui-input--gray4 ${feature.priceLocked ? "ui-input--gray4--disabled" : ""} ui-input--gray4--small text-left`}
            placeholder={intl.formatMessage({ id: "features.setup.feature.placeholder.discount" })}
            onChange={(value) => featureContentInputChange("fabricatorCost", value, fabricatorCost)}
            maxLength={10}
            disabled={feature.priceLocked}
            numberInput={true}
            value={fabricatorCost} />
        </div>
        <div className="fs-feature__line-item fs-feature__line-item--cost">
          {/* Price is an old property that we are NOT using on the frontend. Property is Price, placeholder is Cost, they are swapped // the lowest value */}
          <TextInput className={`ui-input ui-input--gray4 ${costDisabled ? "ui-input--gray4--disabled" : ""} ui-input--gray4--small text-left`}
            placeholder={intl.formatMessage({ id: "features.setup.feature.placeholder.cost" })}
            onChange={(value) => featureContentInputChange("price", value, priceValue)}
            maxLength={10}
            disabled={costDisabled}
            numberInput={true}
            value={priceValue} />
          <FeaturesSetupFeatureContentMarkUpButton feature={feature}
            applyToProps={[MARK_UP_APPLY_TO_TYPES.retailPrice,
            MARK_UP_APPLY_TO_TYPES.retailPriceAndCost]} />
        </div>
        <div className="fs-feature__line-item">
          <FeaturesSetupFeatureContentSku feature={feature} />
        </div>
      </div>
    </div>
  )
};

FeaturesSetupFeatureContent.propTypes = {
  feature: PropTypes.shape(featureInterface).isRequired
}

const mapStateToProps = (store) => {
  const { canEdit } = getFeatureEditPermission(store);
  const AR = AccessRights.get(store);
  const { customerInfo } = store.newaccount;
  const { sharedFeatureNameToggleValue } = store.featuresSetupState;
  return {
    AR,
    canEdit,
    useDifferentCurrencies: customerInfo.useDifferentCurrencies,
    sharedFeatureNameToggleValue
  };
};

const mapDispatchToProps = {
  featuresSetupUpdateFeature,
};

export default injectIntl(
  connect(mapStateToProps, mapDispatchToProps)(FeaturesSetupFeatureContent)
)
